package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun EpisodeEditDialog(
    episode: EpisodeDto,
    apiClient: ApiClient,
    onSave: (UpdateEpisodeRequest, org.w3c.files.File?) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var title by remember { mutableStateOf(episode.title) }
    var description by remember { mutableStateOf(episode.description ?: "") }
    var episodeNumber by remember { mutableStateOf(episode.episodeNumber?.toString() ?: "") }
    var seasonNumber by remember { mutableStateOf(episode.seasonNumber?.toString() ?: "") }
    var status by remember { mutableStateOf(episode.status) }
    var durationSeconds by remember { mutableStateOf(episode.durationSeconds?.toString() ?: "") }
    var audioUrl by remember { mutableStateOf(episode.audioUrl ?: "") }
    var fileSizeBytes by remember { mutableStateOf(episode.fileSizeBytes?.toString() ?: "") }

    // Image states
    var imageUrl by remember { mutableStateOf(episode.imageUrl) }
    var originalImageUrl by remember { mutableStateOf(episode.originalImageUrl) }
    val scope = rememberCoroutineScope()

    // Translation states
    var arTitle by remember { mutableStateOf(episode.translations["ar"]?.title ?: "") }
    var arDescription by remember { mutableStateOf(episode.translations["ar"]?.description ?: "") }
    var arAuthor by remember { mutableStateOf(episode.translations["ar"]?.author ?: "") }

    // Video states
    var videoProvider by remember { mutableStateOf(episode.videoProvider) }
    var videoUrl by remember { mutableStateOf(episode.videoUrl ?: "") }
    var videoId by remember { mutableStateOf(episode.videoId ?: "") }
    var videoUrlError by remember { mutableStateOf<String?>(null) }

    // Upload states
    var selectedFile by remember { mutableStateOf<org.w3c.files.File?>(null) }
    var uploading by remember { mutableStateOf(false) }
    var uploadError by remember { mutableStateOf<String?>(null) }
    var uploadSuccess by remember { mutableStateOf(false) }

    var showConfirmation by remember { mutableStateOf(false) }

    // Modal overlay
    Div({
        style { modalOverlayStyle(colors) }
        onClick { onCancel() }
    }) {
        // Modal content
        Div({
            style { modalContentStyle(colors) }
            onClick { it.stopPropagation() }
        }) {
            H3({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    fontSize(20.px)
                    fontWeight("600")
                }
            }) { Text(if (episode.id.isEmpty()) "Create New Episode" else "Edit Episode") }

            // Note for linked episodes
            if (episode.isLinked) {
                Div({
                    style {
                        padding(Spacing.md)
                        backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                        color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        fontSize(13.px)
                        themeTransition()
                    }
                }) {
                    Text("This episode is synced from RSS. Title, description, and audio are read-only.")
                }
            }

            // Title
            FormField("Title *${if (episode.isLinked) " (from RSS)" else ""}") {
                Input(InputType.Text) {
                    value(title)
                    if (!episode.isLinked) onInput { title = it.value }
                    if (episode.isLinked) attr("readonly", "true")
                    style {
                        formInputStyle(colors)
                        if (episode.isLinked) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Description
            FormField("Description${if (episode.isLinked) " (from RSS)" else ""}") {
                TextArea {
                    value(description)
                    if (!episode.isLinked) onInput { description = it.value }
                    if (episode.isLinked) attr("readonly", "true")
                    style {
                        formInputStyle(colors)
                        height(100.px)
                        property("resize", "vertical")
                        if (episode.isLinked) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Episode Number
            FormField("Episode Number") {
                Input(InputType.Number) {
                    value(episodeNumber)
                    onInput { episodeNumber = it.value?.toString() ?: "" }
                    style { formInputStyle(colors) }
                }
            }

            // Season Number
            FormField("Season Number") {
                Input(InputType.Number) {
                    value(seasonNumber)
                    onInput { seasonNumber = it.value?.toString() ?: "" }
                    style { formInputStyle(colors) }
                }
            }

            // Duration
            FormField("Duration (seconds)") {
                Input(InputType.Number) {
                    value(durationSeconds)
                    onInput { durationSeconds = it.value?.toString() ?: "" }
                    style { formInputStyle(colors) }
                }
            }

            // Status
            FormField("Status") {
                Select({
                    onInput {
                        status = EpisodeStatusDto.valueOf(it.value!!)
                    }
                    style { formInputStyle(colors) }
                }) {
                    Option(EpisodeStatusDto.DRAFT.name, {
                        if (status == EpisodeStatusDto.DRAFT) selected()
                    }) { Text("Draft") }
                    Option(EpisodeStatusDto.PUBLISHED.name, {
                        if (status == EpisodeStatusDto.PUBLISHED) selected()
                    }) { Text("Published") }
                    Option(EpisodeStatusDto.ARCHIVED.name, {
                        if (status == EpisodeStatusDto.ARCHIVED) selected()
                    }) { Text("Archived") }
                }
            }

            // Episode Image Upload (only for existing episodes)
            if (episode.id.isNotEmpty() && !episode.isLinked) {
                ImageUpload(
                    currentImageUrl = imageUrl,
                    originalImageUrl = originalImageUrl,
                    isLinked = episode.isLinked,
                    hasCustomImage = imageUrl != null,
                    label = "Episode Image",
                    onUpload = { file ->
                        // Upload to S3 first
                        val uploadResponse = apiClient.uploadEpisodeImage(episode.id, file)
                        // Then update episode with the URL
                        val updatedEpisode = apiClient.updateEpisodeImage(episode.id, uploadResponse.url)
                        // Update local state
                        imageUrl = updatedEpisode.imageUrl
                        originalImageUrl = updatedEpisode.originalImageUrl
                    },
                    onRemove = {
                        // Remove custom episode image
                        val updatedEpisode = apiClient.removeEpisodeImage(episode.id)
                        // Update local state
                        imageUrl = updatedEpisode.imageUrl
                        originalImageUrl = updatedEpisode.originalImageUrl
                    }
                )
            } else if (episode.id.isEmpty()) {
                // Note for new episodes
                Div({
                    style {
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        fontSize(13.px)
                        color(Color(colors.textMuted))
                        themeTransition()
                    }
                }) {
                    Text("Episode image can be uploaded after creating the episode.")
                }
            } else if (episode.isLinked) {
                // Note for linked episodes
                Div({
                    style {
                        padding(Spacing.md)
                        backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#0c1929"))
                        color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        fontSize(13.px)
                        themeTransition()
                    }
                }) {
                    Text("Episode image is from RSS feed. Custom upload is disabled for linked episodes.")
                }
            }

            // Audio Upload Section
            FormField("Audio File${if (episode.isLinked) " (from RSS)" else ""}") {
                // Current audio URL display
                if (audioUrl.isNotBlank()) {
                    Div({
                        style { infoBoxStyle(colors) }
                    }) {
                        Text("Current: ${audioUrl.takeLast(60)}")
                    }
                }

                // For linked episodes, show that audio is from RSS
                if (episode.isLinked) {
                    Div({
                        style {
                            padding(Spacing.md)
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                            themeTransition()
                        }
                    }) {
                        Text("Audio is streamed from the original RSS source. Upload is disabled for linked episodes.")
                    }
                } else {
                    // Upload status messages
                    uploadError?.let { error ->
                        Div({
                            style {
                                errorMessageStyle(colors)
                                fontSize(Typography.sm)
                            }
                        }) { Text("Error: $error") }
                    }

                    if (uploadSuccess) {
                        Div({
                            style { successMessageStyle(colors) }
                        }) { Text("File uploaded successfully!") }
                    }

                    // File input and upload button
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.sm)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        Input(InputType.File) {
                            id("audio-file-input")
                            attr("accept", "audio/*")
                            if (uploading) disabled()
                            onChange { event ->
                                val input = event.target as org.w3c.dom.HTMLInputElement
                                val file = input.files?.item(0)

                                if (file != null) {
                                    selectedFile = file.unsafeCast<org.w3c.files.File>()
                                    uploadError = null
                                    uploadSuccess = false
                                }
                            }
                            style {
                                flex("1")
                                padding(Spacing.sm)
                                fontSize(Typography.base)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor(if (uploading) "not-allowed" else "pointer")
                            }
                        }

                        if (uploading) {
                            Span({
                                style {
                                    fontSize(Typography.sm)
                                    color(Color(colors.textMuted))
                                }
                            }) { Text("Uploading...") }
                        } else if (selectedFile != null) {
                            Span({
                                style {
                                    fontSize(Typography.sm)
                                    color(Color(colors.success))
                                    fontWeight("500")
                                }
                            }) { Text(selectedFile!!.name) }
                        }
                    }

                    // Note about upload timing
                    Div({
                        style {
                            marginTop(Spacing.sm)
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            fontStyle("italic")
                        }
                    }) {
                        Text(if (selectedFile != null) {
                            "Audio file will be uploaded when you save the episode."
                        } else {
                            "Select an audio file to upload with this episode."
                        })
                    }
                }
            }

            // Video Section
            H4({
                style {
                    fontSize(Typography.lg)
                    fontWeight("600")
                    marginTop(Spacing.xl)
                    marginBottom(Spacing.md)
                }
            }) { Text("Video") }

            // Video Provider Selector
            FormField("Video Provider") {
                Select({
                    onInput {
                        val selectedValue = it.value
                        videoProvider = if (selectedValue.isNullOrBlank() || selectedValue == "NONE") {
                            null
                        } else {
                            VideoProviderDto.valueOf(selectedValue)
                        }
                        // Reset fields when provider changes
                        videoUrl = ""
                        videoId = ""
                        videoUrlError = null
                    }
                    style { formInputStyle(colors) }
                }) {
                    Option("NONE", { if (videoProvider == null) selected() }) { Text("None") }
                    Option(VideoProviderDto.TG.name, { if (videoProvider == VideoProviderDto.TG) selected() }) { Text("TG") }
                    Option(VideoProviderDto.YOUTUBE.name, { if (videoProvider == VideoProviderDto.YOUTUBE) selected() }) { Text("YouTube") }
                    Option(VideoProviderDto.OTHER_URL.name, { if (videoProvider == VideoProviderDto.OTHER_URL) selected() }) { Text("Other URL") }
                }
            }

            // Conditional video fields based on provider
            when (videoProvider) {
                VideoProviderDto.TG -> {
                    FormField("Video ID *") {
                        Input(InputType.Text) {
                            value(videoId)
                            onInput {
                                videoId = it.value
                                videoUrlError = null
                            }
                            attr("placeholder", "Enter TG video ID")
                            style { formInputStyle(colors) }
                        }
                    }
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.xs)
                            fontStyle("italic")
                        }
                    }) { Text("Enter only the video ID for TG") }
                }
                VideoProviderDto.YOUTUBE -> {
                    FormField("YouTube URL *") {
                        Input(InputType.Url) {
                            value(videoUrl)
                            onInput {
                                videoUrl = it.value
                                videoUrlError = null
                                // Basic YouTube URL validation
                                if (it.value.isNotBlank() && !isValidYouTubeUrl(it.value)) {
                                    videoUrlError = "Invalid YouTube URL"
                                }
                            }
                            attr("placeholder", "https://www.youtube.com/watch?v=...")
                            style {
                                formInputStyle(colors)
                                if (videoUrlError != null) {
                                    border(1.px, LineStyle.Solid, Color(colors.danger))
                                }
                            }
                        }
                    }
                    videoUrlError?.let { error ->
                        Div({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.danger))
                                marginTop(Spacing.xs)
                            }
                        }) { Text(error) }
                    }
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.xs)
                            fontStyle("italic")
                        }
                    }) { Text("Enter a valid YouTube URL (youtube.com or youtu.be)") }
                }
                VideoProviderDto.OTHER_URL -> {
                    FormField("Video URL *") {
                        Input(InputType.Url) {
                            value(videoUrl)
                            onInput {
                                videoUrl = it.value
                                videoUrlError = null
                            }
                            attr("placeholder", "https://example.com/video.mp4")
                            style { formInputStyle(colors) }
                        }
                    }
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.xs)
                            fontStyle("italic")
                        }
                    }) { Text("Enter a direct video URL") }
                }
                null -> {
                    Div({
                        style {
                            padding(Spacing.md)
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                            themeTransition()
                        }
                    }) { Text("Select a video provider to add video to this episode") }
                }
            }

            // Arabic Translation Section
            H4({
                style {
                    fontSize(Typography.lg)
                    fontWeight("600")
                    marginTop(Spacing.xl)
                    marginBottom(Spacing.md)
                }
            }) { Text("Arabic Translation") }

            FormField("Arabic Title") {
                Input(InputType.Text) {
                    value(arTitle)
                    onInput { arTitle = it.value }
                    style { formInputStyle(colors) }
                }
            }

            FormField("Arabic Description") {
                TextArea {
                    value(arDescription)
                    onInput { arDescription = it.value }
                    style {
                        formInputStyle(colors)
                        height(100.px)
                        property("resize", "vertical")
                    }
                }
            }

            FormField("Arabic Author") {
                Input(InputType.Text) {
                    value(arAuthor)
                    onInput { arAuthor = it.value }
                    style { formInputStyle(colors) }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    gap(Spacing.md)
                    marginTop(Spacing.xxl)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    style { secondaryButtonStyle(colors) }
                }) { Text("Cancel") }

                Button({
                    onClick { showConfirmation = true }
                    if (title.isBlank()) disabled()
                    style { primaryButtonStyle(disabled = title.isBlank(), colors = colors) }
                }) { Text("Save Changes") }
            }

            // Confirmation Dialog
            if (showConfirmation) {
                ConfirmationDialog(
                    title = "Confirm Changes",
                    message = "Are you sure you want to save these changes to the episode?",
                    onConfirm = {
                        val translations = mutableMapOf<String, EpisodeTranslationData>()
                        if (arTitle.isNotBlank()) {
                            translations["ar"] = EpisodeTranslationData(
                                title = arTitle,
                                description = arDescription.ifBlank { null },
                                author = arAuthor.ifBlank { null }
                            )
                        }

                        onSave(
                            UpdateEpisodeRequest(
                                // Don't send readonly fields for linked episodes
                                title = if (episode.isLinked) null else title,
                                description = if (episode.isLinked) null else description.ifBlank { null },
                                audioUrl = if (episode.isLinked) null else audioUrl.ifBlank { null },
                                episodeNumber = episodeNumber.toIntOrNull(),
                                seasonNumber = seasonNumber.toIntOrNull(),
                                status = status,
                                durationSeconds = durationSeconds.toIntOrNull(),
                                fileSizeBytes = fileSizeBytes.toLongOrNull(),
                                translations = translations,
                                videoProvider = videoProvider,
                                videoUrl = videoUrl.ifBlank { null },
                                videoId = videoId.ifBlank { null }
                            ),
                            selectedFile
                        )
                    },
                    onCancel = { showConfirmation = false }
                )
            }
        }
    }
}

@Composable
fun PodcastTranslationEditDialog(
    language: String,
    translation: PodcastTranslationData,
    podcastId: String,
    isNew: Boolean = false,
    onSave: (PodcastTranslationData) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var title by remember { mutableStateOf(translation.title) }
    var description by remember { mutableStateOf(translation.description ?: "") }
    var author by remember { mutableStateOf(translation.author ?: "") }
    var showConfirmation by remember { mutableStateOf(false) }

    val languageNames = mapOf(
        "en" to "English",
        "ar" to "Arabic",
        "fr" to "French",
        "es" to "Spanish",
        "de" to "German"
    )
    val languageName = languageNames[language] ?: language.uppercase()

    // Modal overlay
    Div({
        style { modalOverlayStyle(colors) }
        onClick { onCancel() }
    }) {
        // Modal content
        Div({
            style { modalContentStyle(colors) }
            onClick { it.stopPropagation() }
        }) {
            H3({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    fontSize(20.px)
                    fontWeight("600")
                }
            }) { Text(if (isNew) "Add $languageName Translation" else "Edit $languageName Translation") }

            // Title
            FormField("Title *") {
                Input(InputType.Text) {
                    value(title)
                    onInput { title = it.value }
                    style { formInputStyle(colors) }
                }
            }

            // Description
            FormField("Description") {
                TextArea {
                    value(description)
                    onInput { description = it.value }
                    style {
                        formInputStyle(colors)
                        height(100.px)
                        property("resize", "vertical")
                    }
                }
            }

            // Author
            FormField("Author") {
                Input(InputType.Text) {
                    value(author)
                    onInput { author = it.value }
                    style { formInputStyle(colors) }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    gap(Spacing.md)
                    marginTop(Spacing.xxl)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    style { secondaryButtonStyle(colors) }
                }) { Text("Cancel") }

                Button({
                    onClick { showConfirmation = true }
                    if (title.isBlank()) disabled()
                    style { primaryButtonStyle(disabled = title.isBlank(), colors = colors) }
                }) { Text(if (isNew) "Add Translation" else "Save Changes") }
            }

            // Confirmation Dialog
            if (showConfirmation) {
                ConfirmationDialog(
                    title = if (isNew) "Confirm Add Translation" else "Confirm Changes",
                    message = if (isNew) "Are you sure you want to add this $languageName translation?" else "Are you sure you want to save these changes to the translation?",
                    onConfirm = {
                        onSave(PodcastTranslationData(
                            title = title,
                            description = description.ifBlank { null },
                            author = author.ifBlank { null }
                        ))
                    },
                    onCancel = { showConfirmation = false }
                )
            }
        }
    }
}

@Composable
fun ImportRssDialog(
    apiClient: ApiClient,
    onImport: (List<PodcastDto>) -> Unit,
    onCancel: () -> Unit,
    defaultType: PodcastType? = null
) {
    val colors = AppTheme.colors
    var rssUrl by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var preview by remember { mutableStateOf<List<RssPreviewResponse>?>(null) }
    var selectedType by remember { mutableStateOf(defaultType ?: PodcastType.PODCAST) }
    val scope = rememberCoroutineScope()

    // Modal overlay
    Div({
        style { modalOverlayStyle(colors) }
        onClick { if (!loading) onCancel() }
    }) {
        // Modal content
        Div({
            style { modalContentStyle(colors) }
            onClick { it.stopPropagation() }
        }) {
            H3({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    fontSize(20.px)
                    fontWeight("600")
                }
            }) { Text("Import Podcast from RSS") }

            // RSS URL Input
            FormField("RSS Feed URL *") {
                Input(InputType.Url) {
                    value(rssUrl)
                    onInput { rssUrl = it.value }
                    attr("placeholder", "https://example.com/podcast/feed.xml")
                    if (loading) disabled()
                    style { formInputStyle(colors) }
                }
            }

            // Podcast Type Selector
            FormField("Podcast Type") {
                Select({
                    onInput { selectedType = PodcastType.valueOf(it.value!!) }
                    if (loading) disabled()
                    style { formInputStyle(colors) }
                }) {
                    PodcastType.values().forEach { type ->
                        Option(type.name, { if (selectedType == type) selected() }) {
                            Text(type.displayName)
                        }
                    }
                }
            }

            // Error message
            error?.let { msg ->
                Div({
                    style {
                        errorMessageStyle(colors)
                        fontSize(Typography.base)
                    }
                }) { Text(msg) }
            }

            // Preview section
            preview?.let { previewList ->
                previewList.forEach { previewData ->
                    Div({
                        style {
                            padding(Spacing.lg)
                            backgroundColor(Color(if (colors == LightColors) "#f0fdf4" else "#052e16"))
                            borderRadius(BorderRadius.md)
                            marginBottom(Spacing.lg)
                            border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#86efac" else "#166534"))
                            themeTransition()
                        }
                    }) {
                        H4({
                            style {
                                margin(0.px, 0.px, Spacing.md, 0.px)
                                fontSize(Typography.lg)
                                fontWeight("600")
                                color(Color(if (colors == LightColors) "#166534" else "#86efac"))
                            }
                        }) { Text("Feed Preview${if (previewList.size > 1) " (${previewList.indexOf(previewData) + 1} of ${previewList.size})" else ""}") }

                    // Cover art and info
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.lg)
                        }
                    }) {
                        // Cover art
                        previewData.coverArtUrl?.let { coverUrl ->
                            Img(src = coverUrl) {
                                style {
                                    width(100.px)
                                    height(100.px)
                                    borderRadius(BorderRadius.lg)
                                    property("object-fit", "cover")
                                }
                            }
                        }

                        // Info
                        Div({
                            style { flex("1") }
                        }) {
                            Div({
                                style {
                                    fontWeight("600")
                                    fontSize(Typography.lg)
                                    marginBottom(Spacing.xs)
                                }
                            }) { Text(previewData.title) }

                            previewData.author?.let {
                                Div({
                                    style {
                                        fontSize(Typography.base)
                                        color(Color(colors.textMuted))
                                        marginBottom(Spacing.xs)
                                    }
                                }) { Text("by $it") }
                            }

                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(Spacing.md)
                                    fontSize(Typography.sm)
                                    color(Color(colors.textMuted))
                                }
                            }) {
                                Span { Text("${previewData.episodeCount} episodes") }
                                Span { Text("Language: ${previewData.language.uppercase()}") }
                                if (previewData.explicit) {
                                    Span({
                                        style { color(Color(colors.danger)) }
                                    }) { Text("Explicit") }
                                }
                            }

                            previewData.description?.let { desc ->
                                val cleanDesc = stripHtmlTags(desc)
                                Div({
                                    style {
                                        marginTop(Spacing.sm)
                                        fontSize(13.px)
                                        color(Color(colors.textMuted))
                                        property("max-height", "60px")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) { Text(cleanDesc.take(200) + if (cleanDesc.length > 200) "..." else "") }
                            }
                        }
                    }
                }
                } // End forEach
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    gap(Spacing.md)
                    marginTop(Spacing.xxl)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    if (loading) disabled()
                    style { secondaryButtonStyle(colors) }
                }) { Text("Cancel") }

                if (preview == null) {
                    // Preview button
                    Button({
                        onClick {
                            scope.launch {
                                loading = true
                                error = null
                                try {
                                    preview = apiClient.previewRssFeed(rssUrl)
                                } catch (e: Exception) {
                                    error = "Failed to fetch RSS feed: ${e.message}"
                                } finally {
                                    loading = false
                                }
                            }
                        }
                        if (rssUrl.isBlank() || loading) disabled()
                        style {
                            padding(Spacing.md, Spacing.xl)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (rssUrl.isBlank() || loading) "not-allowed" else "pointer")
                            fontSize(Typography.base)
                            fontWeight("600")
                            property("opacity", if (rssUrl.isBlank() || loading) "0.6" else "1")
                        }
                    }) { Text(if (loading) "Loading..." else "Preview Feed") }
                } else {
                    // Back button to change URL
                    Button({
                        onClick {
                            preview = null
                            error = null
                        }
                        if (loading) disabled()
                        style { secondaryButtonStyle(colors) }
                    }) { Text("Change URL") }

                    // Import button
                    Button({
                        onClick {
                            scope.launch {
                                loading = true
                                error = null
                                try {
                                    val podcasts = apiClient.importFromRss(rssUrl, selectedType.name)
                                    onImport(podcasts)
                                } catch (e: Exception) {
                                    error = "Failed to import podcast: ${e.message}"
                                } finally {
                                    loading = false
                                }
                            }
                        }
                        if (loading) disabled()
                        style {
                            padding(Spacing.md, Spacing.xl)
                            backgroundColor(Color(colors.success))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (loading) "not-allowed" else "pointer")
                            fontSize(Typography.base)
                            fontWeight("600")
                            property("opacity", if (loading) "0.6" else "1")
                        }
                    }) { Text(if (loading) "Importing..." else "Import Podcast") }
                }
            }
        }
    }
}

@Composable
fun ConfirmationDialog(
    title: String,
    message: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style { confirmationOverlayStyle(colors) }
    }) {
        Div({
            style { confirmationContentStyle(colors) }
        }) {
            H4({
                style {
                    margin(0.px, 0.px, Spacing.md, 0.px)
                    fontSize(Typography.xl)
                    fontWeight("600")
                }
            }) { Text(title) }

            P({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    color(Color(colors.textMuted))
                }
            }) { Text(message) }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    gap(Spacing.md)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    style { secondaryButtonStyle(colors) }
                }) { Text("Cancel") }

                Button({
                    onClick { onConfirm() }
                    style { successButtonStyle(colors) }
                }) { Text("Confirm") }
            }
        }
    }
}
@Composable
fun BulkImportRssDialog(
    apiClient: ApiClient,
    onImport: () -> Unit,
    onCancel: () -> Unit,
    defaultType: PodcastType? = null
) {
    val defaultPodcastType = defaultType ?: PodcastType.PODCAST
    val colors = AppTheme.colors
    var rssUrlsInput by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var previewResults by remember { mutableStateOf<List<RssPreviewResult>?>(null) }
    var selectedUrls by remember { mutableStateOf(mutableSetOf<String>()) }
    var typeSelections by remember { mutableStateOf(mutableMapOf<String, PodcastType>()) }
    var totalUrls by remember { mutableStateOf(0) }
    var processedUrls by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    // Modal overlay
    Div({
        style { modalOverlayStyle(colors) }
        onClick { if (!loading) onCancel() }
    }) {
        // Modal content
        Div({
            style {
                modalContentStyle(colors)
                maxWidth(800.px)
                maxHeight(90.vh)
                property("overflow-y", "auto")
            }
            onClick { it.stopPropagation() }
        }) {
            H3({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    fontSize(20.px)
                    fontWeight("600")
                }
            }) { Text("Bulk Import Podcasts from RSS") }

            if (previewResults == null) {
                // Phase 1: URL Input
                FormField("RSS Feed URLs *") {
                    TextArea {
                        value(rssUrlsInput)
                        onInput { rssUrlsInput = it.value }
                        attr("placeholder", "Paste multiple RSS feed URLs (separated by space, comma, or newline)\n\nExample:\nhttps://example.com/feed1.xml\nhttps://example.com/feed2.xml\nhttps://example.com/feed3.xml")
                        if (loading) disabled()
                        style {
                            formInputStyle(colors)
                            height(150.px)
                            property("resize", "vertical")
                            property("font-family", "monospace")
                        }
                    }
                }

                Div({
                    style {
                        fontSize(13.px)
                        color(Color(colors.textMuted))
                        marginTop(Spacing.sm)
                        marginBottom(Spacing.lg)
                    }
                }) { Text("Supports up to 20 URLs. Separate URLs by space, comma, or newline.") }

                // Error message
                error?.let { msg ->
                    Div({
                        style {
                            errorMessageStyle(colors)
                            fontSize(Typography.base)
                        }
                    }) { Text(msg) }
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.End)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        onClick { onCancel() }
                        if (loading) disabled()
                        style { secondaryButtonStyle(colors) }
                    }) { Text("Cancel") }

                    Button({
                        onClick {
                            scope.launch {
                                loading = true
                                error = null
                                try {
                                    // Parse URLs
                                    val urls = rssUrlsInput
                                        .split(Regex("[\\s,]+"))
                                        .map { it.trim() }
                                        .filter { it.isNotBlank() }

                                    if (urls.isEmpty()) {
                                        error = "Please enter at least one RSS URL"
                                    } else if (urls.size > 3000) {
                                        error = "Maximum 3000 URLs allowed per request"
                                    } else {
                                        // Initialize progress tracking
                                        totalUrls = urls.size
                                        processedUrls = 0
                                        previewResults = emptyList()

                                        // Process URLs one by one
                                        urls.forEach { url ->
                                            try {
                                                val previews = apiClient.previewRssFeed(url)
                                                val result = RssPreviewResult(
                                                    rssUrl = url,
                                                    status = PreviewStatus.SUCCESS,
                                                    previews = previews,
                                                    error = null
                                                )
                                                // Add result to list
                                                previewResults = (previewResults ?: emptyList()) + result
                                                // Auto-select successful URLs
                                                selectedUrls.add(url)
                                                typeSelections[url] = defaultPodcastType
                                            } catch (e: Exception) {
                                                val result = RssPreviewResult(
                                                    rssUrl = url,
                                                    status = PreviewStatus.ERROR,
                                                    previews = null,
                                                    error = e.message ?: "Unknown error"
                                                )
                                                previewResults = (previewResults ?: emptyList()) + result
                                            }
                                            processedUrls++
                                        }
                                    }
                                } catch (e: Exception) {
                                    error = "Failed to preview RSS feeds: ${e.message}"
                                } finally {
                                    loading = false
                                }
                            }
                        }
                        if (rssUrlsInput.isBlank() || loading) disabled()
                        style { primaryButtonStyle(disabled = rssUrlsInput.isBlank() || loading, colors = colors) }
                    }) {
                        Text(
                            if (loading && totalUrls > 0)
                                "Processing $processedUrls/$totalUrls..."
                            else if (loading)
                                "Loading..."
                            else
                                "Preview Feeds"
                        )
                    }
                }

                // Progress indicator
                if (loading && totalUrls > 0) {
                    Div({
                        style {
                            marginTop(Spacing.lg)
                            padding(Spacing.lg)
                            backgroundColor(Color(if (colors == LightColors) "#eff6ff" else "#0c1929"))
                            borderRadius(BorderRadius.md)
                            border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#93c5fd" else "#1e40af"))
                            themeTransition()
                        }
                    }) {
                        Div({
                            style {
                                fontWeight("600")
                                fontSize(Typography.base)
                                color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                                marginBottom(Spacing.sm)
                            }
                        }) { Text("Processing feeds... $processedUrls/$totalUrls") }

                        // Progress bar
                        Div({
                            style {
                                width(100.percent)
                                height(8.px)
                                backgroundColor(Color(if (colors == LightColors) "#dbeafe" else "#1e3a5f"))
                                borderRadius(BorderRadius.sm)
                                property("overflow", "hidden")
                            }
                        }) {
                            Div({
                                style {
                                    height(100.percent)
                                    width((processedUrls.toDouble() / totalUrls * 100).percent)
                                    backgroundColor(Color(colors.primary))
                                    property("transition", "width 0.3s ease")
                                }
                            })
                        }
                    }
                }
            } else {
                // Phase 2: Preview & Selection
                val successCount = previewResults!!.count { it.status == PreviewStatus.SUCCESS }
                val errorCount = previewResults!!.count { it.status == PreviewStatus.ERROR }

                // Summary card
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(if (colors == LightColors) "#f0fdf4" else "#052e16"))
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#86efac" else "#166534"))
                        themeTransition()
                    }
                }) {
                    Div({
                        style {
                            fontWeight("600")
                            fontSize(Typography.lg)
                            marginBottom(Spacing.sm)
                        }
                    }) { Text("Preview Results") }
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(if (colors == LightColors) "#166534" else "#86efac"))
                        }
                    }) { Text("$successCount successful, $errorCount failed") }
                }

                // Results list
                Div({
                    style {
                        property("max-height", "400px")
                        property("overflow-y", "auto")
                        marginBottom(Spacing.lg)
                    }
                }) {
                    previewResults!!.forEach { result ->
                        if (result.status == PreviewStatus.SUCCESS && !result.previews.isNullOrEmpty()) {
                            // Display each preview from this RSS URL
                            result.previews!!.forEach { preview ->
                                val isSelected = selectedUrls.contains(result.rssUrl)

                                Div({
                                    style {
                                        padding(Spacing.lg)
                                        backgroundColor(Color(if (isSelected) {
                                            if (colors == LightColors) "#f0fdf4" else "#052e16"
                                        } else colors.surface))
                                        border(1.px, LineStyle.Solid, Color(if (isSelected) {
                                            if (colors == LightColors) "#86efac" else "#166534"
                                        } else colors.border))
                                        borderRadius(BorderRadius.md)
                                        marginBottom(Spacing.md)
                                        display(DisplayStyle.Flex)
                                        gap(Spacing.lg)
                                        cursor("pointer")
                                        themeTransition()
                                    }
                                    onClick {
                                        if (isSelected) {
                                            selectedUrls.remove(result.rssUrl)
                                        } else {
                                            selectedUrls.add(result.rssUrl)
                                            if (!typeSelections.containsKey(result.rssUrl)) {
                                                typeSelections[result.rssUrl] = defaultPodcastType
                                            }
                                        }
                                    }
                                }) {
                                    // Checkbox (only show for first preview of this URL)
                                    if (preview == result.previews!!.first()) {
                                        Input(InputType.Checkbox) {
                                            checked(isSelected)
                                            onChange {
                                                if (isSelected) {
                                                    selectedUrls.remove(result.rssUrl)
                                                } else {
                                                    selectedUrls.add(result.rssUrl)
                                                    if (!typeSelections.containsKey(result.rssUrl)) {
                                                        typeSelections[result.rssUrl] = PodcastType.PODCAST
                                                    }
                                                }
                                            }
                                            style {
                                                width(20.px)
                                                height(20.px)
                                                cursor("pointer")
                                                property("flex-shrink", "0")
                                            }
                                        }
                                    } else {
                                        // Spacer for alignment
                                        Div({
                                            style {
                                                width(20.px)
                                                height(20.px)
                                                property("flex-shrink", "0")
                                            }
                                        })
                                    }

                                    // Cover art
                                    preview.coverArtUrl?.let { coverUrl ->
                                        Img(src = coverUrl) {
                                            style {
                                                width(60.px)
                                                height(60.px)
                                                borderRadius(BorderRadius.md)
                                                property("object-fit", "cover")
                                                property("flex-shrink", "0")
                                            }
                                        }
                                    }

                                    // Info
                                    Div({
                                        style {
                                            flex("1")
                                            property("min-width", "0")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                fontWeight("600")
                                                fontSize(15.px)
                                                marginBottom(Spacing.xs)
                                            }
                                        }) { Text(preview.title) }

                                        preview.author?.let {
                                            Div({
                                                style {
                                                    fontSize(13.px)
                                                    color(Color(colors.textMuted))
                                                    marginBottom(Spacing.xs)
                                                }
                                            }) { Text("by $it") }
                                        }

                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                gap(Spacing.md)
                                                fontSize(Typography.sm)
                                                color(Color(colors.textMuted))
                                                flexWrap(FlexWrap.Wrap)
                                            }
                                        }) {
                                            Span { Text("${preview.episodeCount} episodes") }
                                            Span { Text("Lang: ${preview.language.uppercase()}") }
                                            if (preview.explicit) {
                                                Span({
                                                    style { color(Color(colors.danger)) }
                                            }) { Text("Explicit") }
                                        }
                                    }

                                    // Type selector (only show when selected)
                                    if (isSelected) {
                                        Div({
                                            style {
                                                marginTop(Spacing.sm)
                                            }
                                        }) {
                                            Select({
                                                onInput {
                                                    typeSelections[result.rssUrl] = PodcastType.valueOf(it.value!!)
                                                }
                                                style {
                                                    formInputStyle(colors)
                                                    width(200.px)
                                                    fontSize(13.px)
                                                }
                                                onClick { it.stopPropagation() }
                                            }) {
                                                PodcastType.values().forEach { type ->
                                                    Option(type.name, {
                                                        if (typeSelections[result.rssUrl] == type) selected()
                                                    }) {
                                                        Text(type.displayName)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            } // Close previews forEach
                        } else {
                            // Error row
                            Div({
                                style {
                                    padding(Spacing.lg)
                                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fecaca" else "#991b1b"))
                                    borderRadius(BorderRadius.md)
                                    marginBottom(Spacing.md)
                                    themeTransition()
                                }
                            }) {
                                Div({
                                    style {
                                        fontWeight("600")
                                        fontSize(Typography.base)
                                        color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                                        marginBottom(Spacing.xs)
                                        property("word-break", "break-all")
                                    }
                                }) { Text(result.rssUrl) }
                                Div({
                                    style {
                                        fontSize(13.px)
                                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                                    }
                                }) { Text("Error: ${result.error ?: "Unknown error"}") }
                            }
                        }
                    }
                }

                // Failed podcasts links section
                val failedResults = previewResults!!.filter { it.status == PreviewStatus.ERROR }
                if (failedResults.isNotEmpty()) {
                    Div({
                        style {
                            padding(Spacing.lg)
                            backgroundColor(Color(if (colors == LightColors) "#fef2f2" else "#450a0a"))
                            border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fecaca" else "#991b1b"))
                            borderRadius(BorderRadius.md)
                            marginBottom(Spacing.lg)
                            themeTransition()
                        }
                    }) {
                        Div({
                            style {
                                fontWeight("600")
                                fontSize(15.px)
                                color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                                marginBottom(Spacing.md)
                            }
                        }) { Text("Failed Podcasts (${failedResults.size})") }

                        Div({
                            style {
                                fontSize(13.px)
                                color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                                property("max-height", "200px")
                                property("overflow-y", "auto")
                            }
                        }) {
                            failedResults.forEach { result ->
                                Div({
                                    style {
                                        padding(Spacing.sm, Spacing.md)
                                        marginBottom(Spacing.xs)
                                        backgroundColor(Color(colors.surface))
                                        borderRadius(BorderRadius.sm)
                                        property("word-break", "break-all")
                                        fontSize(Typography.sm)
                                        color(Color(colors.danger))
                                        themeTransition()
                                    }
                                }) {
                                    Text(result.rssUrl)
                                }
                            }
                        }
                    }
                }

                // Error message
                error?.let { msg ->
                    Div({
                        style {
                            errorMessageStyle(colors)
                            fontSize(Typography.base)
                        }
                    }) { Text(msg) }
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        onClick {
                            previewResults = null
                            selectedUrls.clear()
                            typeSelections.clear()
                            error = null
                        }
                        if (loading) disabled()
                        style { secondaryButtonStyle(colors) }
                    }) { Text("Back") }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.md)
                        }
                    }) {
                        Button({
                            onClick {
                                selectedUrls.clear()
                                previewResults!!
                                    .filter { it.status == PreviewStatus.SUCCESS }
                                    .forEach {
                                        selectedUrls.add(it.rssUrl)
                                        if (!typeSelections.containsKey(it.rssUrl)) {
                                            typeSelections[it.rssUrl] = defaultPodcastType
                                        }
                                    }
                            }
                            if (loading) disabled()
                            style { secondaryButtonStyle(colors) }
                        }) { Text("Select All") }

                        Button({
                            onClick {
                                scope.launch {
                                    loading = true
                                    error = null
                                    try {
                                        val imports = selectedUrls.map { url ->
                                            SingleImportRequest(
                                                rssUrl = url,
                                                type = typeSelections[url]?.name
                                            )
                                        }
                                        val response = apiClient.bulkImportFromRss(imports)
                                        // Show success and close
                                        onImport()
                                    } catch (e: Exception) {
                                        error = "Failed to import podcasts: ${e.message}"
                                    } finally {
                                        loading = false
                                    }
                                }
                            }
                            if (selectedUrls.isEmpty() || loading) disabled()
                            style { primaryButtonStyle(disabled = selectedUrls.isEmpty() || loading, colors = colors) }
                        }) {
                            Text(if (loading) "Importing..." else "Import Selected (${selectedUrls.size})")
                        }
                    }
                }
            }
        }
    }
}

/**
 * Validates if a URL is a valid YouTube URL
 * Supports formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
private fun isValidYouTubeUrl(url: String): Boolean {
    val youtubePatterns = listOf(
        Regex("^(https?://)?(www\\.)?youtube\\.com/watch\\?v=[A-Za-z0-9_-]+(&.*)?$"),
        Regex("^(https?://)?(www\\.)?youtu\\.be/[A-Za-z0-9_-]+$"),
        Regex("^(https?://)?(www\\.)?m\\.youtube\\.com/watch\\?v=[A-Za-z0-9_-]+(&.*)?$"),
        Regex("^(https?://)?(www\\.)?youtube\\.com/embed/[A-Za-z0-9_-]+$")
    )
    return youtubePatterns.any { it.matches(url) }
}
